/**
 * Read-only guard for the question bank editors.
 *
 * Backend writes are gated on the bank's ``bank.<x>.edit`` RBAC permission
 * (``require_permissions`` in ``app/auth/dependencies.py``). This hook resolves
 * the same code so the editor can present that decision instead of letting the
 * user type into fields whose save will be rejected.
 *
 * Fails closed: until ``/auth/me/permissions`` resolves, the editor is
 * read-only. ``PortalLayout`` already warms that query, so an authorised user
 * normally never sees the read-only state.
 */
import { useCallback } from 'react';
import { toast } from 'sonner';
import { usePermissionGate } from '@/auth/permissions';

export const NOT_AUTHORIZED_MESSAGE = 'Not Authorized User';

/**
 * Toast text for a failed bank write.
 *
 * A 403 always reads as "Not Authorized User" regardless of the server's phrasing
 * so the message matches what the read-only editor already told the user. This
 * still matters when the UI thinks the user may edit: permissions are cached for
 * a minute, so a grant revoked mid-session surfaces here first.
 */
export function bankWriteErrorMessage(err, fallback) {
  if (err?.response?.status === 403) return NOT_AUTHORIZED_MESSAGE;
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) {
    return detail.map((e) => e.msg).join(' · ');
  }
  // AuthError serialises as {error, message}.
  if (typeof detail?.message === 'string') return detail.message;
  return err?.message || fallback;
}

export function useBankEditGuard(editPermission) {
  const { allowed, resolved } = usePermissionGate(editPermission);
  const readOnly = !allowed;

  const denyEdit = useCallback(() => {
    toast.error(NOT_AUTHORIZED_MESSAGE);
  }, []);

  /**
   * Wrap a mutating handler. When the user may not edit, the handler never runs
   * and the toast explains why. Use for controls that must stay clickable to be
   * able to report the refusal — e.g. the correct-answer markers, which would
   * silently swallow the click if they were merely disabled.
   */
  const guard = useCallback(
    (fn) => (...args) => {
      if (readOnly) {
        denyEdit();
        return undefined;
      }
      return fn?.(...args);
    },
    [readOnly, denyEdit],
  );

  return { canEdit: allowed, readOnly, resolved, guard, denyEdit };
}

export default useBankEditGuard;
